import { connect } from "react-redux";

import { setToModel } from "../../../Engine/ActionCreators/AllActionCreators";
import {
    highlightComponents,
    unhighlightComponents,
} from "../../../Engine/ActionCreators/HighlightComponentActionCreators";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { withMetrics } from "../../withMetrics";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withVisibilityProps } from "../../withVisibilityProps";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { withTestPageObjectRegistration } from "../../Services/TestPageObjects/PageObjectExposure";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { withFocusOnNextValueEditor } from "../../../Helpers/JumpByEnterOnNextControl/WithFocusOnNextValueEditor";
import { checkIsSuitableToClick, externalOnClickHandler } from "../../../Engine/ActionCreators/ExternalOnClickAction";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { isNotNullOrEmpty, isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import { withDisabledByParent } from "../../withDisabledByParent";
import { WithHighlightedInjectableProps, withHighlighting } from "../../withHighlighting";

import { Input } from "./Input";
import { createNullableMask } from "./InputHelpers";

export interface InputContainerProps extends FormActionHandlerExecutionInjectableProps, WithHighlightedInjectableProps {
    bindingPath: AbsoluteResolvedModelPath;
    disabled?: boolean;
    disabled2?: EvaluableProp<boolean>;
    disabledPure?: boolean;
    viewModelSelect: ViewModelSelect;
    visible?: EvaluableProp<boolean>;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    callHelperOnChange?: FormActionHandler;
    callHelperOnBlur?: FormActionHandler;
    onInnerValueChange?: FormActionHandler;
    mask?: string;
    formatChars?: Record<string, string>;
    rejectInvalidByMaskValue?: boolean;
}

const mapStateToProps = (state: FullFormState, ownProps: InputContainerProps) => {
    const { bindingPath, viewModelSelect, disabledPure, disabled, disabled2, processEvaluableProp, visible } = ownProps;
    const controlModel = viewModelSelect.controlModel(state, bindingPath);
    return {
        disabled: processEvaluableProp(state, disabled2) || disabledPure || (disabled ?? controlModel.disabled),
        isTooltipEnabled: controlModel.isTooltipEnabled,
        value: controlModel.value ?? "",
        error: controlModel.error,
        warning: controlModel.warning,
        ignoreAutoFlag: controlModel.ignoreAutoFlag,
        highlighted: ownProps.highlighted,
        onTooltipClose: ownProps.onTooltipClose,
        visible: processEvaluableProp(state, visible),
        isReadOnly: state.settings.isReadOnly,
        isLink: checkIsSuitableToClick(state.settings, bindingPath),
        nullableMask: createNullableMask(
            controlModel.value,
            isNotNullOrEmpty(controlModel.error) || isNotNullOrEmpty(controlModel.warning),
            ownProps
        ),
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: InputContainerProps) => {
    const { bindingPath } = ownProps;

    return {
        setIgnoreAutoFlag: (value: boolean) => dispatch(setToModel(bindingPath, "ignoreAutoFlag", value)),
        onChange: async (value: string | undefined) => {
            dispatch(setToModel(bindingPath, "value", value));
        },
        onInnerValueChange: async (value: string | undefined) => {
            if (isNotNullOrUndefined(ownProps.onInnerValueChange)) {
                await ownProps.executeFormActionHandler(ownProps.onInnerValueChange, value);
            }
        },
        onChangeWithHelper: async (value: string | undefined) => {
            dispatch(setToModel(bindingPath, "value", value));
            if (isNotNullOrUndefined(ownProps.callHelperOnChange)) {
                await ownProps.executeFormActionHandler(ownProps.callHelperOnChange);
            }
        },
        onBlur: async () => {
            if (isNotNullOrUndefined(ownProps.callHelperOnBlur)) {
                await ownProps.executeFormActionHandler(ownProps.callHelperOnBlur);
            }
        },
        onMouseEnter: () => dispatch(highlightComponents(bindingPath)),
        onMouseLeave: () => dispatch(unhighlightComponents()),
        onClick: () => externalOnClickHandler(dispatch, bindingPath),
    };
};

export const InputContainer = withMetrics(
    withFocusOnNextValueEditor(
        withDisabledByParent(
            withResolvePaths(
                withFormActionHandlerExecution(
                    withViewModelSelect(
                        withEvaluators(
                            withHighlighting(
                                connect(
                                    mapStateToProps,
                                    mapDispatchToProps
                                )(
                                    withVisibilityProps(
                                        withTestPageObjectRegistration(
                                            withFocusManagement(
                                                withExternalMountingEventReporting(withControlDebugInfo(Input))
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            )
        )
    )
);
